import { useTranslation } from 'react-i18next';
import { Button, Modal } from '@/components/ui';
import { CreateLevelFields } from './CreateLevelFields';
import { AssignLevelForm } from './AssignLevelForm';
import type { HierarchyPageVM } from './useHierarchyPage';

export interface LevelAssignmentModalsProps {
    vm: HierarchyPageVM;
}

/**
 * EAA-1818: create-level, create-assignment, deactivate-level and
 * revoke-assignment modals — relocated verbatim from the pre-rebuild
 * HierarchyPage.tsx (EAA-1812), unchanged behaviour. Levels sit below the
 * tree per this ticket's brief, but their CRUD forms are the same real
 * mutations as before.
 */
export function LevelAssignmentModals({ vm }: LevelAssignmentModalsProps) {
    const { t } = useTranslation();
    const {
        levelModalOpen,
        setLevelModalOpen,
        levelForm,
        setLevelForm,
        levelError,
        setLevelError,
        roleKeys,
        roleKeysLoading,
        roleKeysFailed,
        onCreateLevel,
        createLevel,
        assignModalOpen,
        setAssignModalOpen,
        assignForm,
        setAssignForm,
        assignError,
        onCreateAssignment,
        createAssignment,
        activeLevels,
        confirmDeactivateLevel,
        setConfirmDeactivateLevel,
        deactivateLevel,
        confirmRevokeAssignment,
        setConfirmRevokeAssignment,
        revokeAssignment,
    } = vm;

    function closeLevelModal() {
        // EAA-1817: the form state lives on the page, not the modal, so without
        // this a cancelled draft is still sitting in the fields the next time the
        // dialog opens — and the standard-role Select then shows a role the user
        // has already decided against. `onSuccess` clears it the same way.
        setLevelForm({ name: '', role_key: '', description: '' });
        setLevelError(null);
        setLevelModalOpen(false);
    }
    function closeAssignModal() {
        setAssignModalOpen(false);
    }

    return (
        <>
            {/* EAA-1804: kit `Button` instead of hand-written `<button className="portal-btn">`
                across these four dialogs' footers — see AccountDetailModal.tsx for the mapping
                rationale. Submit buttons keep `htmlType="submit"` + the `form` id they already
                targeted; danger buttons keep `danger`. */}
            <Modal
                open={levelModalOpen}
                onCancel={closeLevelModal}
                title={t('console.hierarchy.levelAssignmentModals.createLevelTitle')}
                footer={
                    <>
                        <Button onClick={closeLevelModal}>{t('common.cancel')}</Button>
                        <Button
                            type="primary"
                            htmlType="submit"
                            form="hierarchy-create-level-form"
                            disabled={createLevel.isPending}
                        >
                            {createLevel.isPending
                                ? t('console.hierarchy.levelAssignmentModals.creatingLabel')
                                : t('console.hierarchy.levelAssignmentModals.createLevelTitle')}
                        </Button>
                    </>
                }
            >
                <form
                    id="hierarchy-create-level-form"
                    className="portal-admin-form"
                    onSubmit={onCreateLevel}
                >
                    <CreateLevelFields
                        form={levelForm}
                        setForm={setLevelForm}
                        roleKeys={roleKeys}
                        roleKeysLoading={roleKeysLoading}
                        roleKeysFailed={roleKeysFailed}
                        activeLevels={activeLevels}
                        error={levelError}
                    />
                </form>
            </Modal>

            <Modal
                open={assignModalOpen}
                onCancel={closeAssignModal}
                title={t('console.hierarchy.levelAssignmentModals.assignTitle')}
                footer={
                    <>
                        <Button onClick={closeAssignModal}>{t('common.cancel')}</Button>
                        <Button
                            type="primary"
                            htmlType="submit"
                            form="hierarchy-create-assignment-form"
                            disabled={createAssignment.isPending || activeLevels.length === 0}
                        >
                            {createAssignment.isPending
                                ? t('console.hierarchy.levelAssignmentModals.assigningLabel')
                                : t('console.hierarchy.levelAssignmentModals.assignSubmit')}
                        </Button>
                    </>
                }
            >
                {/* EAA-1839: the three fields moved to the shared `AssignLevelForm`
                    — `/console/hierarchy/accounts`'s "+ Tài khoản" writes the very
                    same endpoint with the very same body, and two copies of one
                    request shape drift. State stays here (this modal's own
                    `assignForm`/`assignError`/`createAssignment` are untouched);
                    only the field markup is shared. */}
                <AssignLevelForm
                    formId="hierarchy-create-assignment-form"
                    value={assignForm}
                    onChange={setAssignForm}
                    levels={activeLevels}
                    error={assignError}
                    onSubmit={onCreateAssignment}
                />
            </Modal>

            <Modal
                open={Boolean(confirmDeactivateLevel)}
                onCancel={() => setConfirmDeactivateLevel(null)}
                title={t('console.hierarchy.levelAssignmentModals.deactivateLevelTitle')}
                footer={
                    <>
                        <Button
                            onClick={() => setConfirmDeactivateLevel(null)}
                            disabled={deactivateLevel.isPending}
                        >
                            {t('common.cancel')}
                        </Button>
                        <Button
                            danger
                            disabled={deactivateLevel.isPending}
                            onClick={() => {
                                if (confirmDeactivateLevel)
                                    deactivateLevel.mutate(confirmDeactivateLevel.id);
                            }}
                        >
                            {deactivateLevel.isPending
                                ? t('console.hierarchy.processingLabel')
                                : t('console.hierarchy.levelAssignmentModals.deactivateLevelConfirm')}
                        </Button>
                    </>
                }
            >
                <p className="portal-confirm-message">
                    {t('console.hierarchy.levelAssignmentModals.deactivateLevelMessage', {
                        name: confirmDeactivateLevel?.name,
                    })}
                </p>
            </Modal>

            <Modal
                open={Boolean(confirmRevokeAssignment)}
                onCancel={() => setConfirmRevokeAssignment(null)}
                title={t('console.hierarchy.levelAssignmentModals.revokeAssignmentTitle')}
                footer={
                    <>
                        <Button
                            onClick={() => setConfirmRevokeAssignment(null)}
                            disabled={revokeAssignment.isPending}
                        >
                            {t('common.cancel')}
                        </Button>
                        <Button
                            danger
                            disabled={revokeAssignment.isPending}
                            onClick={() => {
                                if (confirmRevokeAssignment)
                                    revokeAssignment.mutate(confirmRevokeAssignment.id);
                            }}
                        >
                            {revokeAssignment.isPending
                                ? t('console.hierarchy.processingLabel')
                                : t('console.hierarchy.levelAssignmentModals.revokeButton')}
                        </Button>
                    </>
                }
            >
                <p className="portal-confirm-message">
                    {t('console.hierarchy.levelAssignmentModals.revokeMessage', {
                        identity:
                            confirmRevokeAssignment?.user_email ||
                            confirmRevokeAssignment?.user_sub,
                    })}
                </p>
            </Modal>
        </>
    );
}

export default LevelAssignmentModals;
